import { useContext, useEffect, useRef, useState } from 'react';
import * as Msal from '@azure/msal-browser';
import { buildExternalIdAuthConfig, externalIdScopes } from './ExternalIdAuthConfig';
import Axios from 'axios';
import { User } from '../types/ViewModel/User';
import { useAuthStore } from '../context/AuthContext';
import getContext from '../context/AppSettingsContext';
import { useNavigate } from 'react-router-dom';
import { useVesselStore } from '../context/VesselContext';
import { consumePostLoginRedirect } from '../utils/postLoginRedirect';

// Role value returned by AppsApi's EntraExternalIdController (EidTokenIssuanceStart)
// for external users who have signed up but not yet been approved/assigned a
// company + role. Mirrors AppsDataModel6.Models.Business.AuthRole.Unauthorized.
const UNAUTHORIZED_ROLE = 'Unauthorized';

export const EXTERNAL_SIGNUP_STORAGE_KEY = 'externalIdSignupStatus';
export const EXTERNAL_SIGNUP_PENDING = 'pending_approval';

let externalMsalObj: Msal.PublicClientApplication | null = null;
let externalMsalInitPromise: Promise<void> | null = null;

const getExternalMsalInstance = (config?: Msal.Configuration): Msal.PublicClientApplication => {
    if (!externalMsalObj) {
        if (!config) throw new Error('[ExternalMsal] config required for first initialization');
        externalMsalObj = new Msal.PublicClientApplication(config);
    }
    return externalMsalObj;
};

const ensureInitialized = async (config?: Msal.Configuration): Promise<Msal.PublicClientApplication> => {
    const instance = getExternalMsalInstance(config);
    if (!externalMsalInitPromise) {
        externalMsalInitPromise = instance.initialize();
    }
    await externalMsalInitPromise;
    return instance;
};

const getRoleClaim = (claims?: unknown): string | null => {
    if (!claims) {
        return null;
    }

    // NOTE: "role"/"roles" are in Entra's restricted JWT claim set and can never be
    // populated by a custom claims mapping policy - Entra silently drops them.
    // Our EidTokenIssuanceStart custom claims provider therefore maps the role
    // value to a non-reserved claim type "userRole" instead.
    const typedClaims = claims as { userRole?: string; role?: string; roles?: string[] };
    if (typedClaims.userRole && typedClaims.userRole.length > 0) {
        return typedClaims.userRole;
    }

    if (typedClaims.role && typedClaims.role.length > 0) {
        return typedClaims.role;
    }

    if (typedClaims.roles && typedClaims.roles.length > 0) {
        return typedClaims.roles[0];
    }

    return null;
};

export const hasPendingExternalSignup = (): boolean => sessionStorage.getItem(EXTERNAL_SIGNUP_STORAGE_KEY) === EXTERNAL_SIGNUP_PENDING;

export const hasExternalAccountSession = (): boolean => {
    try {
        return getExternalMsalInstance().getAllAccounts().length > 0;
    } catch {
        return false;
    }
};

export const shouldRedirectToExternalSignup = (): boolean => hasPendingExternalSignup();

export type ExternalSignupState = 'idle' | 'processing' | 'pending_approval' | 'signed_in' | 'error';

const useExternalMsal = () => {
    const { setLoggedInUser, setUserIsUpdated, setIsAuthenticating, setIsVerifyingToken, setToken } = useAuthStore();
    const { setSelectedVessel, setSelectedListVessel } = useVesselStore();
    const appSettings = useContext(getContext());
    // Only build the MSAL config when both fields are present. appsetting.json may be
    // served from a stale cache that pre-dates these keys; treating them as absent is
    // safer than passing undefined to the MSAL constructor and crashing for everyone.
    const externalConfig =
        appSettings.externalAuthClientId && appSettings.externalAuthTenantSubdomain
            ? buildExternalIdAuthConfig(appSettings.externalAuthClientId, appSettings.externalAuthTenantSubdomain)
            : null;
    const navigate = useNavigate();
    const [signupState, setSignupState] = useState<ExternalSignupState>('idle');
    const [isInitialized, setIsInitialized] = useState(false);
    const hasHandledRedirect = useRef(false);
    const isAuthenticatingRef = useRef(false);

    useEffect(() => {
        if (!externalConfig) {
            // External auth fields absent from appsetting.json (stale cache or non-external
            // environment). Mark as initialized so RouterContent's isExternalMsalInitialized
            // guard does not block the internal Odfjell staff auth path.
            setIsInitialized(true);
            return;
        }
        ensureInitialized(externalConfig)
            .then(() => setIsInitialized(true))
            .catch((error) => {
                console.error('External MSAL initialization failed:', error);
            });
    }, []);

    const updateAuthenticatingState = (newValue: boolean) => {
        isAuthenticatingRef.current = newValue;
        setIsAuthenticating(newValue);
    };

    const getExternalBearerToken = (authenticationResult: Msal.AuthenticationResult | null): string => {
        if (!authenticationResult) {
            return '';
        }

        return authenticationResult.accessToken && authenticationResult.accessToken.length > 0 ? authenticationResult.accessToken : authenticationResult.idToken;
    };

    const acquireExternalToken = async (account: Msal.AccountInfo, forceRefresh = false): Promise<Msal.AuthenticationResult | null> => {
        // NOTE: Do NOT check the `isInitialized` React state here. This function is called from
        // inside an Axios request interceptor registered during handleExternalRedirect. At that
        // point the MSAL init promise has already resolved, but React has not yet processed the
        // `setIsInitialized(true)` state update (it is batched/async). Checking `isInitialized`
        // from the closure would always read `false` (stale snapshot), causing every token
        // acquisition to return null and every subsequent API call to fail silently.
        // Awaiting ensureInitialized() is safe — externalMsalInitPromise is already resolved so
        // this resolves instantly without re-initialising anything.
        try {
            const instance = await ensureInitialized(externalConfig ?? undefined);
            return await instance.acquireTokenSilent({
                account,
                scopes: externalIdScopes,
                authority: externalConfig?.auth.authority as string | undefined,
                forceRefresh,
            });
        } catch (error) {
            console.error('External token acquisition failed:', error);

            if (error instanceof Msal.InteractionRequiredAuthError && !isAuthenticatingRef.current) {
                updateAuthenticatingState(true);
                await getExternalMsalInstance().loginRedirect({ scopes: externalIdScopes });
            }

            return null;
        }
    };

    const interceptExternalAxiosRequest = (account: Msal.AccountInfo) => {
        Axios.interceptors.request.use(async (config) => {
            const signalRConnectionJson = localStorage.getItem('signalRConnection');
            const signalRConnection = signalRConnectionJson && JSON.parse(signalRConnectionJson);

            if (signalRConnection?.url && config?.url && sameParam(signalRConnection?.url, config?.url ?? '', 'hub')) {
                config.headers.Authorization = 'Bearer ' + signalRConnection?.accessToken;
            } else {
                const authResult = await acquireExternalToken(account);
                const bearerToken = getExternalBearerToken(authResult);

                if (!bearerToken) {
                    throw new Error('Failed to acquire external token.');
                }

                config.headers.Authorization = 'Bearer ' + bearerToken;
                setToken(bearerToken);
            }

            config.headers.set('Transaction-Id', crypto.randomUUID());
            config.params = { ...config.params, releaseId: appSettings?.releaseId };
            return config;
        });
    };

    const interceptExternalAxiosResponse = () => {
        Axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    setLoggedInUser(null);
                    setSelectedVessel(null);
                    setSelectedListVessel(null);
                    setTimeout(() => navigate('/login'), 0);
                }

                return error;
            }
        );
    };

    const setExternalUser = (authenticationResult: Msal.AuthenticationResult, accountOverride?: Msal.AccountInfo) => {
        const instance = getExternalMsalInstance();
        const activeAccount = accountOverride ?? authenticationResult.account;
        const externalAccount = activeAccount?.username ? instance.getAccountByUsername(activeAccount.username) ?? activeAccount : activeAccount;

        if (!externalAccount) {
            setLoggedInUser(null);
            setUserIsUpdated(true);
            updateAuthenticatingState(false);
            return;
        }

        instance.setActiveAccount(externalAccount);
        const user = new User([], false, externalAccount.name ?? externalAccount.username, externalAccount.username);
        const bearerToken = getExternalBearerToken(authenticationResult);

        if (bearerToken) {
            setToken(bearerToken);
        }

        setLoggedInUser(user);
        setUserIsUpdated(true);
        updateAuthenticatingState(false);
    };

    const checkExternalTokenAndSetUser = async (authenticationResult: Msal.AuthenticationResult, accountOverride?: Msal.AccountInfo) => {
        const activeAccount = accountOverride ?? authenticationResult.account;
        if (!activeAccount) {
            setLoggedInUser(null);
            return;
        }

        interceptExternalAxiosRequest(activeAccount);
        interceptExternalAxiosResponse();
        setExternalUser(authenticationResult, activeAccount);
    };

    const sameParam = (str1: string, str2: string, param: string): boolean => {
        const params1 = new URLSearchParams(new URL(str1).search);
        const params2 = new URLSearchParams(new URL(str2).search);
        return params1.has(param) && params2.has(param) && params1.get(param) === params2.get(param);
    };

    // Starts (or resumes) the Microsoft Entra External ID hosted sign-up/sign-in
    // experience. Redirects the browser away from OrcaWebsite.
    const loginExternal = async () => {
        const instance = await ensureInitialized(externalConfig ?? undefined);
        await instance.loginRedirect({ scopes: externalIdScopes });
    };

    const logoutExternal = async (): Promise<void> => {
        const instance = await ensureInitialized(externalConfig ?? undefined);
        await instance.logoutRedirect({ postLogoutRedirectUri: externalConfig?.auth.postLogoutRedirectUri });
    };

    const completeApprovedExternalSignIn = async (account: Msal.AccountInfo, authenticationResult?: Msal.AuthenticationResult): Promise<boolean> => {
        const tokenResult = authenticationResult ?? (await acquireExternalToken(account));
        if (!tokenResult) {
            return false;
        }

        await checkExternalTokenAndSetUser(tokenResult, account);
        sessionStorage.removeItem(EXTERNAL_SIGNUP_STORAGE_KEY);
        return true;
    };

    // Handles the redirect back from Entra External ID after the user
    // completes (or abandons) the custom-authentication-extension sign-up
    // flow. Must be called once on mount of the sign-up landing page.
    const handleExternalRedirect = async (): Promise<ExternalSignupState> => {
        if (hasHandledRedirect.current) {
            return signupState;
        }
        hasHandledRedirect.current = true;
        setSignupState('processing');

        try {
            const instance = await ensureInitialized(externalConfig ?? undefined);
            const result = await instance.handleRedirectPromise();

            const account = result?.account ?? instance.getAllAccounts()[0];
            if (!account) {
                setSignupState('idle');
                return 'idle';
            }

            instance.setActiveAccount(account);
            const role = getRoleClaim(result?.idTokenClaims ?? account.idTokenClaims);

            // New/pending external users do not yet have an application role claim.
            // AppsApi may also return the explicit Unauthorized role for this state.
            if (!role || role === UNAUTHORIZED_ROLE) {
                sessionStorage.setItem(EXTERNAL_SIGNUP_STORAGE_KEY, EXTERNAL_SIGNUP_PENDING);
                // Intentionally do NOT logoutRedirect here. That call performs a full
                // redirect logout against the CIAM tenant and wipes the cached account
                // out of MSAL's sessionStorage cache entirely. Since RouterContent's
                // hasExternalAccountSession() / checkExternalAuthStatus() rely on that
                // cached account to silently re-check the role once an admin approves
                // the user (via a forceRefresh acquireTokenSilent call - see
                // checkExternalAuthStatus), logging out here would permanently strand
                // the user on /signup with no way to detect a later approval short of
                // an entirely new interactive sign-in. The account stays cached but the
                // app-level loggedInUser state is never set, so no access is granted.
                setSignupState('pending_approval');
                return 'pending_approval';
            }

            const signedIn = await completeApprovedExternalSignIn(account, result ?? undefined);
            if (!signedIn) {
                console.error('[ExternalMSAL] handleExternalRedirect: completeApprovedExternalSignIn returned false');
                setSignupState('error');
                return 'error';
            }

            setSignupState('signed_in');
            const target = consumePostLoginRedirect() ?? '/report-events';
            setTimeout(() => navigate(target), 0);
            return 'signed_in';
        } catch (error) {
            console.error('[ExternalMSAL] handleExternalRedirect: exception', error);
            setSignupState('error');
            return 'error';
        }
    };

    const checkExternalAuthStatus = async (): Promise<ExternalSignupState> => {
        if (!isInitialized) {
            return 'idle';
        }

        setIsVerifyingToken(true);

        try {
            const instance = await ensureInitialized(externalConfig ?? undefined);
            const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

            if (!account) {
                setIsVerifyingToken(false);
                return 'idle';
            }

            instance.setActiveAccount(account);

            // Force a fresh token acquisition rather than trusting the account's cached
            // idTokenClaims. The cached ID token was issued at the time of the original
            // sign-up (before admin approval), so its role claim is permanently stale
            // for the lifetime of that cached token. forceRefresh re-runs the custom
            // authentication extension's token issuance against the CIAM tenant, which
            // reflects the user's *current* AuthRole/company assignment.
            const freshTokenResult = await acquireExternalToken(account, true);
            if (!freshTokenResult) {
                sessionStorage.setItem(EXTERNAL_SIGNUP_STORAGE_KEY, EXTERNAL_SIGNUP_PENDING);
                setIsVerifyingToken(false);
                return 'pending_approval';
            }

            const role = getRoleClaim(freshTokenResult.idTokenClaims);

            if (!role || role === UNAUTHORIZED_ROLE) {
                sessionStorage.setItem(EXTERNAL_SIGNUP_STORAGE_KEY, EXTERNAL_SIGNUP_PENDING);
                setIsVerifyingToken(false);
                return 'pending_approval';
            }

            const signedIn = await completeApprovedExternalSignIn(account, freshTokenResult);
            setIsVerifyingToken(false);
            return signedIn ? 'signed_in' : 'error';
        } catch (error) {
            console.error('[ExternalMSAL] checkExternalAuthStatus: exception', error);
            setIsVerifyingToken(false);
            return 'error';
        }
    };

    return {
        loginExternal,
        logoutExternal,
        handleExternalRedirect,
        checkExternalAuthStatus,
        hasExternalAccountSession,
        signupState,
        isExternalMsalInitialized: isInitialized,
    };
};

export default useExternalMsal;
